import { formatCurrency } from './formatters';
import type { CreatorTerms } from '../types';

/**
 * Pick the currently-active terms row out of a creator's full version history.
 *
 * A creator's `terms` array is the complete history, newest-first not guaranteed
 * (docs §2.2). The active row is the one that has not been superseded AND whose
 * `startsAt`/`endsAt` bracket today — the same rule the API uses everywhere else.
 *
 * Note docs §1.1 warns against computing this on the creators *list* screen; it is
 * only appropriate where we've fetched one creator in full (§2.3).
 *
 * Returns `undefined` when the creator has no terms yet, or when every version has
 * expired — both are real states, and a creator needs active terms before they can
 * create a discount code at all.
 */
export const resolveCurrentTerms = (
  terms?: CreatorTerms[],
  now: Date = new Date()
): CreatorTerms | undefined => {
  if (!terms?.length) return undefined;
  const time = now.getTime();
  return terms.find((row) => {
    if (row.supersededById !== null) return false;
    const startsAt = new Date(row.startsAt).getTime();
    const endsAt = new Date(row.endsAt).getTime();
    return startsAt <= time && time <= endsAt;
  });
};

/** Sort a terms history newest-first for display. */
export const sortTermsByStart = (terms?: CreatorTerms[]): CreatorTerms[] =>
  [...(terms ?? [])].sort(
    (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
  );

/** Human-readable allowance, e.g. "15%" or "$5.00". */
export const formatAllowance = (terms: CreatorTerms): string =>
  terms.allowanceType === 'percent'
    ? `${terms.allowanceValue}%`
    : formatCurrency(terms.allowanceValue);
